/**
 * Elegir talla y color al vender.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  SIN ESTA PANTALLA, LAS TALLAS NO SIRVEN PARA NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se pueden crear 20 casillas en el catálogo, cargarles existencia una por una
 * y cuidarlas — y si al vender el cajero toca «Camisa» y se va, la venta no
 * dice qué talla salió y el inventario de todas se queda igual. El trabajo de
 * llevar tallas se pierde entero en el mostrador.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE VE LA EXISTENCIA DE CADA CASILLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque ES la razón de tener tallas. «Me quedan 20 camisas» no le sirve a
 * nadie cuando la señora pide una M negra: lo que hay que saber es si queda
 * ESA. Aquí se ve sin preguntar y sin ir a otra pantalla.
 *
 * Las agotadas se enseñan igual, en gris y con un 0 — no se esconden. Que el
 * cajero pueda decir «de esa no me queda, ¿le sirve la L?» vale más que una
 * cuadrícula limpia. Y se pueden tocar: hay negocios que venden por encargo, y
 * el que no quiera lo apaga con «no vender sin existencia», que ya existe.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { cx } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { catalogo, inventario } from "@/lib/endpoints";
import type { ItemListRow } from "@/lib/types";
import { descargarPaginado } from "./descargarPaginado";

type Props = {
  /** Solo hacen falta el id y el nombre: lo demás se pide aquí dentro. */
  item: Pick<ItemListRow, "id" | "name">;
  locationId: string | null;
  onElegir: (variantId: string, nombre: string) => void;
  onCerrar: () => void;
};

export function SelectorDeTalla({ item, locationId, onElegir, onCerrar }: Props) {
  const { data: detalle, isLoading } = useQuery({
    queryKey: ["item", item.id],
    queryFn: () => catalogo.obtener(item.id),
  });

  // ⚠️ Se piden las existencias de ESTE producto, no las de todo el inventario.
  // Una tienda con 800 casillas no puede traérselas enteras cada vez que el
  // cajero toca una camisa.
  //
  // ⚠️ TODAS sus casillas, no la primera página. Se pedía una sola página
  // creyendo que un producto no llega a cincuenta tallas, y la de defecto era
  // de cincuenta: un zapato del 34 al 45 en seis colores son setenta y dos, y
  // de la cincuenta y uno en adelante salían en cero — agotadas sin estarlo.
  const { data: existencias } = useQuery({
    queryKey: ["stock-de-item", item.id, locationId],
    queryFn: () =>
      descargarPaginado(
        (page) =>
          inventario.stock({ item_id: item.id, location_id: locationId, page, size: 200 }),
        { porPagina: 200, tope: 10_000 },
      ),
    staleTime: 15_000,
  });

  const porVariante = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of existencias ?? []) m.set(f.variant_id, Number(f.qty_on_hand));
    return m;
  }, [existencias]);

  const casillas = useMemo(
    () => (detalle?.variants ?? []).filter((v) => v.is_active),
    [detalle],
  );

  // Los valores de cada eje, en el orden en que están guardados.
  const { filas, columnas } = useMemo(() => {
    const f: string[] = [];
    const c: string[] = [];
    for (const v of casillas) {
      if (v.valor_1 && !f.includes(v.valor_1)) f.push(v.valor_1);
      if (v.valor_2 && !c.includes(v.valor_2)) c.push(v.valor_2);
    }
    return { filas: f, columnas: c };
  }, [casillas]);

  const conDosEjes = columnas.length > 0;

  function buscar(v1: string, v2: string | null) {
    return casillas.find((v) => v.valor_1 === v1 && (v2 === null || v.valor_2 === v2));
  }

  return (
    <Modal
      abierto
      titulo={item.name}
      descripcion={
        conDosEjes
          ? `Toca la casilla: ${detalle?.eje_1_nombre ?? "talla"} y ${detalle?.eje_2_nombre ?? "color"}.`
          : `Toca la ${(detalle?.eje_1_nombre ?? "talla").toLocaleLowerCase()}.`
      }
      onCerrar={onCerrar}
      ancho="md"
    >
      {isLoading && <p className="py-6 text-center text-sm text-muted">Cargando…</p>}

      {/* ── Un solo eje: una rejilla de botones ── */}
      {!isLoading && !conDosEjes && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {casillas.map((v) => (
            <Casilla
              key={v.id}
              etiqueta={v.valor_1 ?? v.name ?? ""}
              cantidad={porVariante.get(v.id)}
              onClick={() => onElegir(v.id, v.name ?? "")}
            />
          ))}
        </div>
      )}

      {/* ── Dos ejes: la cuadrícula, que es como está la mercancía ── */}
      {!isLoading && conDosEjes && (
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-1">
            <thead>
              <tr>
                <th />
                {columnas.map((c) => (
                  <th
                    key={c}
                    className="whitespace-nowrap px-1 pb-1 text-xs font-medium text-muted"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f}>
                  <th className="whitespace-nowrap pr-2 text-right text-xs font-semibold text-ink">
                    {f}
                  </th>
                  {columnas.map((c) => {
                    const v = buscar(f, c);
                    if (!v) {
                      // Esa combinación no existe en este producto: no todas las
                      // camisas vienen en todos los colores.
                      return (
                        <td key={c} className="text-center text-xs text-faint">
                          —
                        </td>
                      );
                    }
                    return (
                      <td key={c}>
                        <Casilla
                          etiqueta=""
                          cantidad={porVariante.get(v.id)}
                          onClick={() => onElegir(v.id, v.name ?? "")}
                          titulo={v.name ?? ""}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && casillas.length === 0 && (
        <p className="py-6 text-center text-sm text-muted">
          Este producto no tiene tallas activas.
        </p>
      )}
    </Modal>
  );
}

/**
 * Una casilla tocable con su existencia.
 *
 * El número ES el contenido, no un adorno al lado: en una cuadrícula de 20
 * casillas lo que el cajero busca de un vistazo es dónde hay y dónde no.
 */
function Casilla({
  etiqueta,
  cantidad,
  onClick,
  titulo,
}: {
  etiqueta: string;
  cantidad: number | undefined;
  onClick: () => void;
  titulo?: string;
}) {
  const hay = (cantidad ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      // 44px de alto: se toca con el pulgar, de pie y con prisa.
      className={cx(
        "flex h-11 w-full min-w-[44px] flex-col items-center justify-center rounded-lg border px-1 transition-colors",
        hay
          ? "border-line bg-surface hover:border-accent hover:bg-accent-soft"
          : "border-dashed border-line bg-transparent text-faint",
      )}
    >
      {etiqueta && (
        <span className={cx("text-sm font-medium leading-none", hay ? "text-ink" : "text-faint")}>
          {etiqueta}
        </span>
      )}
      <span
        className={cx(
          "tabular leading-none",
          etiqueta ? "mt-0.5 text-[10px]" : "text-sm font-semibold",
          hay ? "text-muted" : "text-faint",
        )}
      >
        {cantidad ?? 0}
      </span>
    </button>
  );
}
